import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {Spinner, Form} from "react-bootstrap";
import {Formik} from "formik";
import * as Yup from 'yup';

import FormButton from "../../components/formik/elements/Button"
import surveyApi from "../../api/surveyApi";
import questionApi from "../../api/questionApi";
import Attendee from "../answer/Attendee";
import Answer from "../answer/Answer";
import CountrySelect from "../answer/CountrySelect";
import ProgressBar from "../../components/progress/ProgressBar";
import ProgressBarQuestionnaires from "../../components/progress/ProgressBarQuestionnaires";
import {getQuestionnaireIndexByName} from "../../api/utils";
import QuestionnaireHeading from "../../components/formik/elements/QuestionnaireHeading";
import LandingPage from "../landing/LandingPage";
import ErrorAlert from "../error/ErrorAlert";

const SurveyAttempt = ({match, surveyId}) => {

    const localSurveyId = surveyId ? surveyId : match.params.surveyId;

    const history = useHistory();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalQuestionsCount, setTotalQuestionsCount] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState();
    const [questionHistory, setQuestionHistory] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    const [progressCompleted, setProgressCompleted] = useState(0);
    const [questionnaires, setQuestionnaires] = useState([]);
    const [error, setError] = useState("");

    // fetch total question count
    useEffect(() => {
        setIsLoading(true);
        questionApi.fetchSurveyQuestionsCount(localSurveyId)
        .then(({data}) => setTotalQuestionsCount(data))
        .finally(() => setIsLoading(false));
    }, [localSurveyId]);

    // fetch first question
    useEffect(() => {
        setIsLoading(true);
        questionApi.fetchSurveyFirstQuestion(localSurveyId)
        .then(({data}) => setCurrentQuestion(data))
        .finally(() => setIsLoading(false));
    }, [localSurveyId]);

    // fetch questionnaires (for headings / progress)
    useEffect(() => {
        setIsLoading(true);
        surveyApi.fetchSurveyQuestionnaires(localSurveyId)
        .then(({data}) => setQuestionnaires(data))
        .finally(() => setIsLoading(false));
    }, [localSurveyId]);

    // If attendee was “invitedAttendeeCode,” fetch them
    // useEffect(() => {
    //   // Removed the entire block that fetched invitedAttendee
    // }, [...]);

    // const handleAttendeeFound = (attendee) => {
    //   // Removed the logic that handled existing or finished attendee
    // };

    // form initial state
    const initialState = {
        answers: [
            {
                id: null,
                value: '',
            }
        ],
        attendee: {
            companyName: '',
            companyWebsite: '',
            attendeeName: '',
            email: '',
            phoneNo: '',
            policyAgree: false,
            recommendationsAgree: false,
            attendeeCode: '',
            isExport: false,
        },
        country: null,
    };

    // final validation
    // validate Attendee fields at the final step
    const validationSchema = Yup.object().shape({
        attendee: Yup.object().shape({
            companyName: Yup.string().required('Įmonės pavadinimas yra privalomas'),
            attendeeName: Yup.string().required('Vardas/pavardė yra privalomi'),
            email: Yup.string()
            .email('Netinkamas el. pašto formatas')
            .required('El. paštas yra privalomas'),
            phoneNo: Yup.string()
            .matches(/^\+?[\d\s-]{7,20}$/, 'Netinkamas telefono numerio formatas')
            .nullable(),
            policyAgree: Yup.bool().oneOf([true], 'Būtina pažymėti'),
            isExport: Yup.string().required('Būtina pasirinkti „Taip“ ar „Ne“'),
        }),
        country: Yup.object().test('is-country-selected', 'Būtina pasirinkti', (val) => !!val),
    });

    // following steps:
    // step 0: LandingPage
    // step 1: CountrySelect
    // step 2..(2 + totalQuestionsCount -1): questions
    // step 2 + totalQuestionsCount: final (Attendee)
    // final step index = totalQuestionsCount + 2
    const isAtLanding = (currentIndex === 0);
    const isAtCountrySelect = (currentIndex === 1);
    const isWithinQuestions = currentIndex >= 2 && currentIndex < (totalQuestionsCount + 2 - 1);
    // final step index => totalQuestionsCount + 1 => Attendee form
    const isAtAttendeeForm = (currentIndex === totalQuestionsCount + 1);

    const nextIndex = currentIndex + 1;

    // for the progress bar
    function updateProgress(question) {
        const maxSteps = totalQuestionsCount + 2;
        const defaultIncrement = 100 / maxSteps;
        const stepNo = question.questionsRemaining ? maxSteps - question.questionsRemaining : question;
        setProgressCompleted(Math.round(stepNo * defaultIncrement));
    }

    function nextQuestion(formik) {
        setIsValidating(true);
        formik.validateForm().then((errors) => {
            setIsValidating(false);

            // final step validation
            if (isAtAttendeeForm && errors.attendee) {
                return;
            }

            const formValues = formik.values;
            const answers = formValues.answers;
            // in questions, question should be answered
            if (isWithinQuestions) {
                const ansObj = answers[currentIndex];
                if (!ansObj?.id) {
                    return;
                }
            }

            // for landing page and country select
            if (isAtLanding || isAtCountrySelect) {
                setCurrentIndex(nextIndex);
                updateProgress(nextIndex);
                return;
            }

            // from questions => either next question or final step
            if (isWithinQuestions) {
                setIsLoadingQuestion(true);
                questionApi.fetchSurveyNextQuestion(localSurveyId, syncToCurrentPointInHistory(formValues))
                .then(({data: suggestedNextQuestion}) => {
                    // validations to ensure proper question history flow
                    if (suggestedNextQuestion) {
                        if (wasNextQuestionPreviously(suggestedNextQuestion)) {
                            mountNextQuestionFromHistory();
                        } else {
                            if (hasDiscoveredNewConditionalQuestion(suggestedNextQuestion, answers)) {
                                injectQuestionToHistory(suggestedNextQuestion, answers);
                            }
                            if (hasSkippedConditionalQuestions(suggestedNextQuestion)) {
                                forgetSkippedQuestions(suggestedNextQuestion, answers);
                            }
                            advanceWithQuestion(suggestedNextQuestion);
                        }
                    } else {
                        // no more questions => go to final step (Attendee)
                        setCurrentIndex(totalQuestionsCount + 1);
                        updateProgress(totalQuestionsCount + 1);
                    }
                })
                .finally(() => setIsLoadingQuestion(false));
                return;
            }
            // if at final step => we are done, submit
            if (isAtAttendeeForm) {
                handleSubmitAll(formValues);
            }
        });
    }

    // final submission
    function handleSubmitAll(values) {
        console.log("Final payload:", values)
        if (values.country) {
            values.attendee.targetCountry = values.country.label;
        }
        surveyApi.storeNewSurveyAttempt(values, localSurveyId)
        .then((response) => {
            // after completing all survey, it should redirect to /apklausa/${attendee.attendeeCode}/ivertinimas
            // that will be generated and returned with last responses
            const newAttendeeCode = response.data;
            history.push(`/apklausa/${newAttendeeCode}/ivertinimas`);
        });
    }

    // when attempting to advance within question history
    // ensures that only answers up to this point are considered for next-question-check
    // because otherwise API would check against full answer bundle
    // since Formik contains all answered values, regardless of history point that user is currently in
    const syncToCurrentPointInHistory = (fullRequest) => {
        const slicedRequest = {...fullRequest};
        slicedRequest.answers = slicedRequest.answers.slice(0, nextIndex);
        return slicedRequest;
    };

    function advanceWithQuestion(question) {
        setCurrentQuestion(question);
        setCurrentIndex(nextIndex);
        updateProgress(question);
    }

    // saves current question to question history if not already saved
    //  (by checking if that slot (historyPoint) is available)
    function saveCurrentQuestionToHistory() {
        if (!questionHistory.some(historyPoint => historyPoint.answerIndex === currentIndex)) {
            setQuestionHistory(prevState => [...prevState, {answerIndex: currentIndex, question: currentQuestion}]);
        }
    }

    // when iterating through question history, answer changes may impact future question visibility
    // e.g. a changed answer may trigger some new question to be shown/hidden in place where it was/wasn't previously
    // this function checks if passed to-be-next question has also been next previously according to question history
    function wasNextQuestionPreviously(nextQuestion) {
        const nextQuestionPreviously = questionHistory.find(previousQuestion => previousQuestion.answerIndex === nextIndex)?.question;
        return nextQuestionPreviously && nextQuestionPreviously.id === nextQuestion.id;
    }

    // this function advances with a next, already answered question from question history
    // rather than advancing with a fetched one from API
    function mountNextQuestionFromHistory() {
        const nextQuestionFromHistory = questionHistory.find(historyPoint => historyPoint.answerIndex === nextIndex);
        if (nextQuestionFromHistory) advanceWithQuestion(nextQuestionFromHistory.question);
    }

    // in case after changing the answers - suggested next question is different from what was there before
    // this function evaluates if this unexpected question is a fresh new question (rather than skipped to)
    function hasDiscoveredNewConditionalQuestion(suggestedNextQuestion, answers) {
        const questionAtNextIndexBefore = questionHistory.find(historyPoint => historyPoint.answerIndex === (nextIndex))
            || answers[nextIndex];
        return questionAtNextIndexBefore
            && !hasSkippedConditionalQuestions(suggestedNextQuestion)
            && suggestedNextQuestion.id !== questionAtNextIndexBefore.id;
    }

    // in case after changing the answers - suggested next question is different from what was there before
    // this function evaluates if this unexpected question is a result of skipped questions (rather than being fresh new)
    function hasSkippedConditionalQuestions(suggestedNextQuestion) {
        const questionEntryInHistory = questionHistory.find(h => h.question.id === suggestedNextQuestion.id);
        return questionEntryInHistory && questionEntryInHistory.answerIndex !== (nextIndex);
    }

    // if changing answers resulted some questions to be skipped
    // this function removes them from history and rearranges indexes
    function forgetSkippedQuestions(suggestedNextQuestion, answers) {
        const indexPreviously = questionHistory.find(historyPoint => historyPoint.question.id === suggestedNextQuestion.id).answerIndex;
        const indexesToForget = questionHistory
        .filter(historyPoint => historyPoint.answerIndex >= (nextIndex) && historyPoint.answerIndex < indexPreviously)
        .map(historyPoint => historyPoint.answerIndex);
        const updatedHistory = questionHistory.filter(historyPoint => !indexesToForget.includes(historyPoint.answerIndex));
        updatedHistory.forEach(historyPoint => {
            // shift question indexes left to take place of removed ones
            if (historyPoint.answerIndex > indexesToForget[0]) {
                historyPoint.answerIndex -= indexesToForget.length;
            }
        });
        setQuestionHistory(updatedHistory);
        answers.splice(nextIndex, indexesToForget.length);
    }

    // if changing answers resulted a new question to emerge WITHIN history,
    // this function embeds it at that exact point
    function injectQuestionToHistory(question, answers) {
        const currentHistory = [...questionHistory];
        currentHistory.forEach(historyPoint => {
            if (historyPoint.answerIndex >= nextIndex) {
                historyPoint.answerIndex += 1;
            }
        });
        currentHistory.push({answerIndex: nextIndex, question: question});
        setQuestionHistory(currentHistory);
        answers.splice(nextIndex, 0, {id: undefined});
    }

    function previousQuestion() {
        if (currentIndex <= 1) {
            setCurrentIndex(currentIndex - 1);
            return;
        }
        const previousQuestion = questionHistory.find(h => h.answerIndex === (currentIndex - 1));
        if (previousQuestion) {
            setCurrentIndex(currentIndex - 1);
            setCurrentQuestion(previousQuestion.question);
            updateProgress(currentIndex - 1);
        }
    }

    const isNotAnswered = (formik) => {
        const answer = formik.values.answers[currentIndex];
        return !answer?.id;
    };

    function getQuestionnaireName() {
        return currentQuestion?.questionnaireName || '';
    }
    function getQuestionnaireNumber() {
        return getQuestionnaireIndexByName(questionnaires, getQuestionnaireName()) + 1;
    }

    return (
        <div className="container-fluid col-xl-7 mb-4">
            {isLoading &&
                <Spinner animation="border" role="status" className="m-5">
                    <span className="sr-only">Loading...</span>
                </Spinner>}
            {error && <ErrorAlert error={error}/>}
            {!error && !isLoading && (
                <Formik
                    initialValues={initialState}
                    validationSchema={validationSchema}
                    onSubmit={() => {/* handleSubmitAll is called from nextQuestion */}}
                >
                    {(formik) => (
                        <Form>
                            {/* Show progress bar except on landing page */}
                            {!isAtLanding && (
                                <>
                                    <ProgressBar completed={progressCompleted}/>
                                    <ProgressBarQuestionnaires
                                        currentQuestionnaireName={currentQuestion?.questionnaireName}
                                        allQuestionnaires={questionnaires}
                                    />
                                </>
                            )}

                            {/* step 0: Landing */}
                            {isAtLanding && (
                                <LandingPage onStart={() => nextQuestion(formik)}/>
                            )}

                            {/* step 1: CountrySelect */}
                            {isAtCountrySelect && (
                                <CountrySelect
                                    formik={formik}
                                    handleClick={() => nextQuestion(formik)}
                                />
                            )}

                            {/* steps 2.. (2 + totalQuestionsCount -1): questions */}
                            {isWithinQuestions && currentQuestion && (
                                <div className="progress__content-offset">
                                    <QuestionnaireHeading
                                        prefix={`TEMA #${getQuestionnaireNumber()}:`}
                                        text={getQuestionnaireName()}
                                    />
                                    <Answer
                                        formik={formik}
                                        question={currentQuestion}
                                        questionnaireId="1"
                                        index={currentIndex}
                                        saveQuestionToHistory={saveCurrentQuestionToHistory}
                                    />

                                    {currentIndex > 2 && (
                                        <FormButton
                                            text="Grįžti"
                                            onClick={() => previousQuestion()}
                                            disabled={currentIndex <= 2}
                                            type="button"
                                            mr
                                        />
                                    )}
                                    <FormButton
                                        text="Tęsti"
                                        onClick={() => nextQuestion(formik)}
                                        disabled={isNotAnswered(formik)}
                                        type="button"
                                    />

                                    {isLoadingQuestion && (
                                        <Spinner
                                            animation="border"
                                            role="status"
                                            className="ml-3"
                                        >
                                            <span className="sr-only">Loading...</span>
                                        </Spinner>
                                    )}
                                </div>
                            )}

                            {/* final step: index = totalQuestionsCount + 1 => Attendee form */}
                            {isAtAttendeeForm && (
                                <div className="progress__content-offset">
                                    <Attendee formik={formik} isValidating={isValidating}/>
                                    <div className="mt-3">
                                        <FormButton
                                            text="Išsaugoti"
                                            onClick={() => nextQuestion(formik)}
                                            disabled={formik.isSubmitting}
                                            type="button"
                                        />
                                    </div>
                                </div>
                            )}
                        </Form>
                    )}
                </Formik>
            )}
        </div>
    );
};

export default SurveyAttempt;
